import React, {useState} from "react";
import {formatCurrency, formatNumber} from "../utils/numberFormat";
import Dropdown from "./Dropdown";
import CarIcon from "../assets/icons/CarIcon";
import PinIcon from "../assets/icons/PinIcon";
import PriceIcon from "../assets/icons/PriceIcon";
import ColourIcon from "../assets/icons/ColourIcon";
import VehicleIcon from "../assets/icons/VehicleIcon";
import GearBoxIcon from "../assets/icons/GearBoxIcon";
import FuelIcon from "../assets/icons/FuelIcon";
import CarDoorIcon from "../assets/icons/CarDoorIcon";
import DistanceIcon from "../assets/icons/DistanceIcon";
import TextField from "./TextField";
import PostCodeIcon from "../assets/icons/PostCodeIcon";
import {AdvertApiResponse} from "../types/ApiResponse";
import {Advert} from "../types/Advert";
import {Make} from "../types/Make";
import {Model} from "../types/Model";
import {Location} from "../types/Location";
import {Colour} from "../types/Colour";
import {BodyStyle} from "../types/BodyStyle";
import {FuelType} from "../types/FuelType";
import {DISTANCES, GEARBOX_TYPES, PRICES, SORT_BY_FIELDS} from "../data/defaults";
import usePrevious from "../hooks/usePrevious";
import useNoInitialEffect from "../hooks/useNoInitialRender";
import {fetchAdverts} from "../api/api";
import {sumKeyValues} from "../utils/sumKeyValues";
import {RequestParams} from "../api/RequestParams";
import "../styles/search.scss";
import {useDispatch, useSelector} from "react-redux";
import {RootStore} from "../store/rootStore";
import {setCurrentPage} from "../store/reducers/searchResultReducer";
import useDebouncedEffect from "../hooks/useDebouncedEffect";

function recordToArray<T extends any>(r: Record<any, any>) {
    return Object.keys(r)?.map(i => ({slug: i, name: i} as T))
}

type SearchBoxProps = {
    expanded?: boolean;
    onSubmitHandler: (params?: RequestParams, apiResponse?: AdvertApiResponse<Advert>) => void;
}

export const SearchBox: React.FC<SearchBoxProps> = ({expanded, onSubmitHandler}) => {
    const {sortField, filters, pageSize, matchedCars, cachedResponse} =
        useSelector((store: RootStore) => {
            return {
                filters: store.searchResult.filters,
                pageSize: store.searchResult.pageSize,
                sortField: store.searchResult.sortField,
                matchedCars: store.searchResult.resultCount,
                cachedResponse: store.searchResult.cachedResponse
            }
        });

    const [initialStateChanged, setInitialStateChanged] = useState(false);
    const [cachedApiResponse, setCachedApiResponse] = useState<AdvertApiResponse<Advert> | null | undefined>(cachedResponse)
    const [matchedCarsCount, setMatchedCarsCount] = useState<number>(matchedCars);
    const dispatch = useDispatch();

    const {
        facetModels,
        facetBodyStyles,
        facetColours,
        facetDoors,
        facetFuelTypes,
        facetTransmission
    } = useSelector((store: RootStore) => {
        const {
            model,
            "body-style": bodyStyle,
            "fuel-type": fuelType,
            colour,
            doors,
            transmission
        } = store.searchResult.cachedResponse?.meta.filters!;

        return {
            facetModels: recordToArray<Model>(model ?? []),
            facetBodyStyles: recordToArray<BodyStyle>(bodyStyle ?? []),
            facetColours: recordToArray<Colour>(colour ?? []),
            facetFuelTypes: recordToArray<FuelType>(fuelType ?? []),
            facetDoors: doors ? Object.keys(doors) : [],
            facetTransmission: recordToArray<{ id: string, name: string }>(transmission ?? []),
        };
    });

    const [params, setParams] = useState<RequestParams>();

    const [makes, setMakes] = useState<Make[]>((window as any).makes.filter((m: Make) => m.standard));
    const [models, setModels] = useState<Model[]>(facetModels);
    const [locations, setLocations] = useState<Location[]>((window as any).locations);
    const [colours, setColours] = useState<Colour[]>(facetColours.length ? facetColours : (window as any).colours.filter((c: Colour) => c.standard));
    const [bodyStyles, setBodyStyles] = useState<BodyStyle[]>(facetBodyStyles.length ? facetBodyStyles : (window as any).bodyStyles);
    const [fuelTypes, setFuelTypes] = useState<FuelType[]>(facetFuelTypes.length ? facetFuelTypes : (window as any).fuelTypes);
    const [prices, setPrices] = useState<number[]>(PRICES);
    const [gearBoxTypes, setGearBoxTypes] = useState<typeof GEARBOX_TYPES>(facetTransmission.length ? facetTransmission : GEARBOX_TYPES);
    const [distances] = useState<number[]>(DISTANCES)
    const [doors, setDoors] = useState<string[]>(facetDoors);
    const [make, setMake] = useState(filters.make);
    const [model, setModel] = useState(filters.model);
    const [location, setLocation] = useState(filters["geo.location"]);
    const [colour, setColour] = useState(filters.colour);
    const [bodyStyle, setBodyStyle] = useState(filters["body-style"]);
    const [fuelType, setFuelType] = useState(filters["fuel-type"]);
    const [price, setPrice] = useState(filters.price);
    const [doorsCount, setDoorsCount] = useState(filters.doors);
    const [postCode, setPostCode] = useState(filters["geo.postcode"]);
    const [distance, setDistance] = useState(filters["geo.miles"]);
    const [transmission, setTransmission] = useState(filters.transmission);
    const [isApproved, setIsApproved] = useState(filters["dealer.isApproved"]);
    const [modelDropdownDisabled, setModelDropdownDisabled] = useState(false)
    const [colourDropdownDisabled, setColourDropdownDisabled] = useState(false)
    const [bodyStyleDropdownDisabled, setBodyStyleDropdownDisabled] = useState(false)
    const [fuelTypeDropdownDisabled, setFuelTypeDropdownDisabled] = useState(false)
    const [doorsCountDropdownDisabled, setDoorsCountDropdownDisabled] = useState(false)
    const [transmissionDropdownDisabled, setTransmissionDropdownDisabled] = useState(false)
    const [advancedSearch, setAdvancedSearch] = useState(expanded);

    const [loggedIn, setLoggedIn] = useState((window as any).loggedIn);

    const [resultLoading, setResultLoading] = useState(false);

    const previousMake = usePrevious<typeof make>(make);
    const previousModel = usePrevious<typeof model>(model);
    const previousBodyStyle = usePrevious<typeof bodyStyle>(bodyStyle);
    const previousColour = usePrevious<typeof colour>(colour);
    const previousFuelType = usePrevious<typeof fuelType>(fuelType);
    const previousTransmission = usePrevious<typeof transmission>(transmission);
    const previousDoorsCount = usePrevious<typeof doorsCount>(doorsCount);

    function loadAdverts() {
        const queryParams = getParams();

        if (make !== previousMake && (model || colour || bodyStyle || transmission || fuelType || doorsCount)) {
            if ((initialStateChanged && make !== previousMake) || (!initialStateChanged && make !== undefined)) {
                setModel(undefined);
                setColour(undefined);
                setBodyStyle(undefined);
                setTransmission(undefined);
                setFuelType(undefined);
                setDoorsCount(undefined);
                return;
            }
        }

        if (previousModel == model) setModelDropdownDisabled(true);
        if (previousBodyStyle == bodyStyle) setBodyStyleDropdownDisabled(true);
        if (previousColour == colour) setColourDropdownDisabled(true);
        if (previousFuelType == fuelType) setFuelTypeDropdownDisabled(true);
        if (previousTransmission == transmission) setTransmissionDropdownDisabled(true);
        if (previousDoorsCount == doorsCount) setDoorsCountDropdownDisabled(true);

        fetchAdverts(queryParams).then(r => {
            setCachedApiResponse(r);
            setMatchedCarsCount(sumKeyValues(r.meta.filters.make));

            if (previousModel == model || model == undefined) {
                if (r.meta.filters.model) {
                    setModels(Object.keys(r.meta.filters.model)?.map(i => ({slug: i, name: i} as Model)));
                }
                !model && setModel(undefined);
            }

            if (previousBodyStyle == bodyStyle || bodyStyle == undefined) {
                setBodyStyles(Object.keys(r.meta.filters["body-style"]).map(i => ({
                    slug: i,
                    name: i
                } as BodyStyle)));
                !bodyStyle && setBodyStyle(undefined);
            }

            if (previousColour == colour || colour == undefined) {
                setColours(Object.keys(r.meta.filters.colour).map(i => ({slug: i, name: i} as Colour)));
                !colour && setColour(undefined);
            }

            if (previousFuelType == fuelType || fuelType == undefined) {
                setFuelTypes(Object.keys(r.meta.filters["fuel-type"]).map(i => ({slug: i, name: i} as FuelType)));
                !fuelType && setFuelType(undefined);
            }

            if (previousTransmission == transmission || transmission == undefined) {
                setGearBoxTypes(Object.keys(r.meta.filters.transmission).map(i => ({id: i, name: i})));
                !transmission && setTransmission(undefined);
            }

            if (previousDoorsCount == doorsCount || doorsCount == undefined) {
                setDoors(Object.keys(r.meta.filters.doors));
                !doorsCount && setDoorsCount(undefined);
            }
        }).finally(() => {
            setModelDropdownDisabled(false);
            setBodyStyleDropdownDisabled(false);
            setColourDropdownDisabled(false);
            setFuelTypeDropdownDisabled(false);
            setTransmissionDropdownDisabled(false);
            setDoorsCountDropdownDisabled(false);
            setParams(queryParams);
        });
    }

    useNoInitialEffect(() => {
        loadAdverts();
    }, [make,
        model,
        location,
        bodyStyle,
        fuelType,
        price,
        colour,
        doorsCount,
        isApproved,
        distance,
        transmission
    ]);

    useDebouncedEffect(() => {
        if (postCode === "" || (postCode && /^[A-Za-z]{1,2}\d{1,2}[A-Za-z]?\s?\d[A-Za-z]{2}$/.test(postCode))) {
            loadAdverts();
        }
    }, [postCode]);

    useNoInitialEffect(() => {
        if (!make &&
            !model &&
            !location &&
            !bodyStyle &&
            !fuelType &&
            !price &&
            !colour &&
            !doorsCount &&
            !postCode &&
            !isApproved &&
            !distance &&
            !transmission) {
            setInitialStateChanged(false);
        } else {
            setInitialStateChanged(true);
        }
    }, [
        make, model, location, bodyStyle, fuelType, price, colour, doorsCount, postCode, isApproved, distance, transmission
    ]);

    function getParams() {
        return new RequestParams({
                make: make!,
                model: model,
                colour: colour,
                price: price,
                doors: doorsCount,
                "body-style": bodyStyle,
                "fuel-type": fuelType,
                transmission: transmission,
                "geo.location": location,
                "geo.postcode": postCode,
                "geo.miles": distance,
                "dealer.isApproved": isApproved,
            },
            {
                field: sortField,
                direction: SORT_BY_FIELDS.find(i => i.id == sortField)?.desc ? "desc" : "asc"
            },
            {
                number: 1,
                size: pageSize
            }
        );
    }

    function navigateToResultPage() {
        if (initialStateChanged) {
            dispatch(setCurrentPage(1));
            onSubmitHandler(params, cachedApiResponse!);
        } else {
            onSubmitHandler(undefined, undefined);
        }
    }

    function resetForm() {
        setMake(undefined);
        setModel(undefined);
        setLocation(undefined);
        setColour(undefined);
        setPrice(0);
        setBodyStyle(undefined);
        setTransmission(undefined);
        setFuelType(undefined);
        setDoorsCount(undefined);
        setDistance(0);
        setPostCode(undefined);

        setColours([]);
        setModels([]);
        setBodyStyles([]);
        setFuelTypes([]);
        setGearBoxTypes([]);
        setDoors([]);
        setCachedApiResponse(undefined);
    }

    return (
        <div className={["col-12 col-md-8 col-lg-7 col-xl-5 search-box-container",
            advancedSearch ? "expanded" : ""].join(" ")}>
            <div className="search-box-wrapper d-flex flex-column align-items-center py-3 py-md-4 px-3 px-md-5">
                <form action="" id="search-box-form" className="d-flex flex-column w-100">
                    <div className="form-row">
                        <div className="form-group w-100">
                            <Dropdown
                                name="makesDropdown"
                                label="Make"
                                value={make}
                                data={makes}
                                valueMember={"name"}
                                defaultItem={{name: "All makes"}}
                                icon={<CarIcon/>}
                                onChange={(value) => {
                                    setMake(value);
                                }}
                            />
                        </div>

                        <div className="form-group w-100">
                            <Dropdown
                                name="modelsDropdown"
                                label="Model"
                                value={model}
                                data={models}
                                defaultItem={{name: "All models"}}
                                icon={<CarIcon/>}
                                disabled={modelDropdownDisabled}
                                onChange={(value) => {
                                    setModel(value);
                                }}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group w-100">
                            <Dropdown
                                name="locationsDropdown"
                                label="Location"
                                value={location}
                                data={locations}
                                valueMember={"slug"}
                                defaultItem={{id: 999999999, name: "Nationwide"}}
                                icon={<PinIcon/>}
                                onChange={(value) => {
                                    setLocation(value);
                                }}
                            />
                        </div>

                        <div className="form-group w-100">
                            <Dropdown
                                name="priceDropdown"
                                label="Price"
                                data={prices}
                                value={price}
                                defaultItem={{name: "Any Price"}}
                                valueFormatter={(value) => {
                                    return `Up to ${formatCurrency(value)}`;
                                }}
                                icon={<PriceIcon/>}
                                onChange={(value) => {
                                    setPrice(value);
                                }}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group w-100">
                            <Dropdown
                                name="colourDropdown"
                                label="Colour"
                                data={colours}
                                value={colour}
                                defaultItem={{name: "Any Colour"}}
                                disabled={colourDropdownDisabled}
                                icon={<ColourIcon/>}
                                onChange={(value) => {
                                    setColour(value);
                                }}
                            />
                        </div>

                        <div className="form-group w-100">
                            <Dropdown
                                name="bodyStyleDropdown"
                                label="Body Style"
                                data={bodyStyles}
                                value={bodyStyle}
                                defaultItem={{name: "Any Body Style"}}
                                disabled={bodyStyleDropdownDisabled}
                                icon={<VehicleIcon/>}
                                onChange={(value) => {
                                    setBodyStyle(value);
                                }}
                            />
                        </div>
                    </div>

                    <div className="advanced-search-wrapper" aria-hidden={!advancedSearch}>
                        <div className="advanced-search">
                            <div className="form-row">
                                <div className="form-group w-100">
                                    <Dropdown
                                        name="gearBoxDropdown"
                                        label="Gear Box"
                                        data={gearBoxTypes}
                                        value={transmission}
                                        defaultItem={{name: "All"}}
                                        disabled={transmissionDropdownDisabled}
                                        icon={<GearBoxIcon/>}
                                        onChange={(value) => {
                                            setTransmission(value);
                                        }}
                                    />
                                </div>
                                <div className="form-group w-100">
                                    <Dropdown
                                        name="fuelTypeDropdown"
                                        label="Fuel Type"
                                        data={fuelTypes}
                                        value={fuelType}
                                        defaultItem={{name: "Any"}}
                                        disabled={fuelTypeDropdownDisabled}
                                        icon={<FuelIcon/>}
                                        onChange={(value) => {
                                            setFuelType(value);
                                        }}
                                    />
                                </div>
                                <div className="form-group w-100">
                                    <Dropdown
                                        name="doorsDropdown"
                                        label="Doors"
                                        data={doors}
                                        value={doorsCount}
                                        defaultItem={{name: "Any"}}
                                        disabled={doorsCountDropdownDisabled}
                                        icon={<CarDoorIcon/>}
                                        onChange={(value) => {
                                            setDoorsCount(value);
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group w-100">
                                    <Dropdown
                                        name="distanceDropdown"
                                        label="Distance"
                                        data={distances}
                                        value={distance}
                                        defaultItem={{name: "Nationwide"}}
                                        icon={<DistanceIcon/>}
                                        valueFormatter={(v) => {
                                            return `${v} mile${v > 1 ? "s" : ""}`
                                        }}
                                        onChange={(value) => {
                                            setDistance(value);
                                        }}
                                    />
                                </div>
                                <div className="form-group w-100">
                                    <TextField
                                        name="postCode"
                                        label="Post Code"
                                        value={postCode}
                                        placeholder={"e.g. SK8 2JY"}
                                        icon={<PostCodeIcon/>}
                                        onChange={(value) => {
                                            setPostCode(value);
                                        }}
                                    />
                                </div>
                                {loggedIn &&
                                    <div className="form-group w-100">
                                        <div className="form-check form-check-inline mt-4">
                                            <input className="form-check-input" type="checkbox" id="inlineCheckbox1"
                                                   checked={isApproved} onChange={(value) => {
                                                setIsApproved(isApproved ? false : true);
                                            }}/>
                                            <label className="form-check-label" htmlFor="inlineCheckbox1">Just approved
                                                dealers</label>
                                        </div>
                                    </div>}
                            </div>
                        </div>
                    </div>

                    <div className="form-row">
                        <button
                            type="button"
                            style={{pointerEvents: !matchedCarsCount && initialStateChanged ? "none" : "all"}}
                            className={["btn btn-lg btn-submit-form w-100 my-2", matchedCarsCount ? "expanded" : ""].join(" ")}
                            onClick={() => {
                                navigateToResultPage();
                            }}>
                            {resultLoading && <i className="loader"></i>}
                            <span className="d-flex align-items-center"
                                  style={{opacity: resultLoading ? 0 : 1}}>
                                <svg width="23" height="24" viewBox="0 0 23 24" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M9.83648 0C4.39839 0 0 4.47699 0 10.0123C0 15.5475 4.39839 20.0245 9.83648 20.0245C11.778 20.0245 13.5749 19.4448 15.0983 18.4601L20.5409 24L23 21.4969L17.6478 16.0675C18.909 14.3834 19.673 12.2968 19.673 10.0123C19.673 4.47699 15.2746 0 9.83648 0ZM9.83648 2.35583C14.0021 2.35583 17.3585 5.77224 17.3585 10.0123C17.3585 14.2523 14.0021 17.6687 9.83648 17.6687C5.67089 17.6687 2.31447 14.2523 2.31447 10.0123C2.31447 5.77224 5.67089 2.35583 9.83648 2.35583Z"
                                        fill="#18261D"
                                    />
                                </svg>
                                {matchedCarsCount === undefined ? "Search" : ` ${!matchedCarsCount && initialStateChanged ? "No matched cars" : "Show"}`}
                                <span><strong>&nbsp;{formatNumber(matchedCarsCount!)}</strong> {`Car${+matchedCarsCount! > 1 ? "s" : ""}`}</span>
                            </span>
                        </button>
                    </div>

                    {!expanded &&
                        <div className="form-row align-self-center justify-content-between w-100 my-1 my-md-0">
                            <button
                                type="button"
                                id="reset-form-btn"
                                className={"my-n1"}
                                style={{visibility: initialStateChanged ? "visible" : "hidden"}}
                                onClick={() => {
                                    resetForm();
                                }}
                            >
                                Reset
                            </button>

                            <button
                                type="button"
                                id="expand-search-btn"
                                className={"text-white my-n1"}
                                onClick={() => setAdvancedSearch((current) => !current)}
                            >
                                <svg style={{marginRight: "8px"}} width="22" height="22" viewBox="0 0 22 22"
                                     fill="#8399a1"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M11.5 11.4452L7.08972 6.77117C6.61187 6.26475 5.83623 6.26475 5.35839 6.77117C4.88054 7.27759 4.88054 8.09961 5.35839 8.60603L10.684 14.25C11.1353 14.7283 11.8659 14.7283 12.316 14.25L17.6416 8.60603C18.1195 8.09961 18.1195 7.27759 17.6416 6.77117C17.1638 6.26475 16.3881 6.26475 15.9103 6.77117L11.5 11.4452Z"
                                        fill="#8FB7C1"
                                    />
                                </svg>
                                <span>{advancedSearch ? "Basic Search" : "Advanced Search"}</span>
                            </button>
                        </div>}
                </form>
            </div>
        </div>
    )
}